import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, AlertCircle, Shield, Users, Zap, ArrowRight, UserPlus, CheckCircle2, UserX } from "lucide-react";
import { cn } from "@/lib/utils";
import authService from "@/services/auth.service";
import useAuthStore from "@/stores/authStore";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Alert, AlertDescription } from "./ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { useTranslation } from 'react-i18next';

/** Landing hero'suyla birebir aynı dönen kelimeler (tek metin düğümü). */
const heroWords = ['beantworten', 'verkaufen', 'versenden', 'lösen'];

const Login = () => {
  const { t } = useTranslation();
  const [wordIndex, setWordIndex] = useState(0);
  useEffect(() => {
    const rotationTimer = setInterval(() => {
      setWordIndex((current) => (current + 1) % heroWords.length);
    }, 1600);
    return () => clearInterval(rotationTimer);
  }, []);
  const navigate = useNavigate();
  const rememberedEmail = localStorage.getItem('rememberEmail') || '';
  const [email, setEmail] = useState(rememberedEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isAccountDeactivated, setIsAccountDeactivated] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);

  const { setUser, setIsAuthenticated } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await authService.login({ email, password });

      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);

        // Remember me functionality
        if (rememberMe) {
          localStorage.setItem('rememberEmail', email);
        } else {
          localStorage.removeItem('rememberEmail');
        }

        navigate(response.data.user?.role === 'warehouse' ? '/warehouse' : '/tickets');
      }
    } catch (error: any) {
      if (error.response?.data?.code === 'ACCOUNT_DEACTIVATED') {
        setIsAccountDeactivated(true);
        setError("");
      } else if (error.response?.status === 429) {
        setError("Zu viele Anmeldeversuche. Bitte versuchen Sie es in 15 Minuten erneut.");
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError("Ungültige E-Mail-Adresse oder Passwort");
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-white flex">
      {/* Sol pano — landing hero'sunun birebir portu (light, sola hizalı) */}
      <div className="login-hero hidden lg:flex lg:w-[58%] flex-col justify-between bg-white p-12">
        <div>
          <img src="/brand/login-logo.png" alt="Vyndesk" className="h-7 w-auto" />
        </div>

        <div>
          <span className="vh-eyebrow">
            <span className="vh-try-badge">{t('auth:login.welcomeBack', 'Willkommen zurück')}</span>
            <span className="vh-eyebrow-label">{t('auth:login.platformLabel', 'KI-gestützte Support-Plattform')}</span>
          </span>

          <h1 aria-label="Eine Support-Plattform für alles, was Sie beantworten.">
            <span className="vh-title-line">Eine Support-Plattform</span>
            <span className="vh-title-line vh-title-line--second">
              für alles, was Sie{' '}
              <span className="vh-rotating-slot" aria-hidden="true">
                <span className="vh-rotating-word" key={heroWords[wordIndex]}>
                  <span className="vh-rotating-letters">{heroWords[wordIndex]}</span>
                  <span className="vh-rotating-period">.</span>
                </span>
              </span>
            </span>
          </h1>

          <p className="vh-subtitle">
            {t('auth:login.heroSubtitle', 'Ein Posteingang, eine KI, die Ihre Bestelldaten kennt — für E-Mail, WhatsApp, Formular, Instagram, Facebook, Chatbot und Telefon.')}
          </p>
        </div>

        <div className="text-xs text-slate-400">
          Vyndesk © {new Date().getFullYear()} · Alle Rechte vorbehalten.
        </div>
      </div>

      {/* Sağ pano — form (yeni tema: keskin köşeler, siyah CTA) */}
      <div className="flex-1 flex flex-col justify-center items-center border-l border-[rgba(16,20,28,0.07)] bg-white px-8 sm:px-12 lg:px-16">
        <div className="w-full max-w-[400px] space-y-7">
          {/* Mobilde logo (sol pano gizliyken) */}
          <div className="text-center lg:hidden">
            <img src="/brand/login-logo.png" alt="Vyndesk" className="h-8 w-auto mx-auto" />
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-400">Vyndesk Desk</div>
            <h2 className="mt-2 text-2xl font-bold text-[#101319]">{t('auth:login.signInHeading', 'Anmelden bei Vyndesk')}</h2>
            <p className="mt-2 text-sm text-slate-500">{t('auth:login.signInSub', 'Zugriff auf Tickets, Bestellungen, Anrufe und Meetings.')}</p>
          </div>

          {error && (
            <Alert variant="destructive" className="rounded-[4px] border-red-200 bg-red-50 text-red-900">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700">{error}</AlertDescription>
            </Alert>
          )}

          {isAccountDeactivated && (
            <div className="rounded-[4px] border border-amber-200 bg-amber-50 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <UserX className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-900">{t('auth:login.disabled', 'Konto deaktiviert')}</h3>
                  <p className="text-sm text-amber-700">{t('auth:login.disabledHint', 'Ihr Zugang wurde vorübergehend deaktiviert.')}</p>
                </div>
              </div>
              <p className="text-sm text-amber-800 leading-relaxed">
                Bitte wenden Sie sich an einen Administrator, um Ihr Konto wieder zu aktivieren.
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full border-amber-300 text-amber-800 hover:bg-amber-100"
                onClick={() => { setIsAccountDeactivated(false); setPassword(""); }}
              >
                {t('common:action.backToLogin', 'Zurück zur Anmeldung')}
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit} className={cn("space-y-5", isAccountDeactivated && "hidden")}>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">{t('auth:login.email', 'E-Mail')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth:login.emailPh', 'name@company.com')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 rounded-[4px] bg-white border-[rgba(16,20,28,0.12)] focus:border-[#0F99FD] focus:ring-[#0F99FD]/20 transition-all"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">{t('common:field.password', 'Passwort')}</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-xs font-medium text-[#0b7ad1] hover:text-[#101319] transition-colors"
                >
                  {t('auth:forgot.title', 'Passwort vergessen?')}
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 rounded-[4px] bg-white border-[rgba(16,20,28,0.12)] focus:border-[#0F99FD] focus:ring-[#0F99FD]/20 transition-all"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                className="rounded-[3px] data-[state=checked]:bg-[#101319] data-[state=checked]:border-[#101319]"
              />
              <Label htmlFor="remember" className="text-sm font-medium text-slate-600 cursor-pointer select-none">
                {t('auth:login.remember', 'Angemeldet bleiben')}
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full h-11 rounded-[4px] bg-[#101319] hover:bg-black text-white font-semibold transition-all"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('common:x1.signingIn', 'Wird angemeldet...')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {t('auth:login.title', 'Anmelden')} <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>

          {!isAccountDeactivated && (
          <div className="border-t border-[rgba(16,20,28,0.07)] pt-5 text-center">
            <p className="text-sm text-slate-500">
              {t('auth:login.noAccount', 'Noch kein Konto?')}{' '}
              <button
                type="button"
                onClick={() => setIsRegisterModalOpen(true)}
                className="font-semibold text-[#0b7ad1] hover:text-[#101319] transition-colors"
              >
                {t('auth:login.register', 'Registrieren')}
              </button>
            </p>
          </div>
          )}
        </div>
      </div>

      {/* Register Info Modal */}
      <Dialog open={isRegisterModalOpen} onOpenChange={setIsRegisterModalOpen}>
        <DialogContent className="sm:max-w-[420px] p-0 overflow-hidden border-0 shadow-xl rounded-2xl">
          <div className="bg-slate-50 p-6 border-b border-slate-100 flex flex-col items-center text-center">
            <div className="h-12 w-12 bg-blue-50 rounded-full flex items-center justify-center mb-4">
              <UserPlus className="h-6 w-6 text-[#0F99FD]" />
            </div>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {t('common:x1.registration', 'Registrierung')}
            </DialogTitle>
            <DialogDescription className="text-slate-500 mt-1">
              {t('common:x1.infoForNewUsers', 'Informationen für neue Benutzer')}
            </DialogDescription>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
              <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">{t('auth:login.agentOnly', 'Agent-Zugang erforderlich')}</p>
                <p className="text-blue-700/80 leading-relaxed">
                  Aus Sicherheitsgründen werden Zugänge nur durch Administratoren erstellt. 
                  Bitte wenden Sie sich an Ihre IT-Abteilung.
                </p>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <span>{t('auth:login.f1', 'Zentralisierte Benutzerverwaltung')}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <span>{t('auth:login.f2', 'Rollenbasierte Zugriffskontrolle')}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <span>{t('auth:login.f3', 'Sichere 2FA-Optionen')}</span>
              </div>
            </div>

            <Button
              onClick={() => setIsRegisterModalOpen(false)}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white mt-4"
            >
              {t('common:x1.understood', 'Verstanden')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
